import { find, path, toPairs } from 'ramda'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import Icon from '@/components/icon'
import { breakpoints, colors, fontWeights, fontSizes } from '@/styles'
import toArray from '@/utils/to-array'
import { Reveal } from '@/components/animations'
import useMediaQuery from '@/hooks/use-media-query'
import {
  CreditUtilization,
  EmploymentVerification,
  IdentityVerification,
  IncomeVerification,
  SocialVerification,
  PaymentRecord,
  CollectionsRecord,
  BankruptcyIndex
} from './info-modals'
import { BubbleTitle } from './elements'

const StyledReveal = styled(Reveal)`
  &[aria-expanded='false'] {
    margin: 0 52px;
    @media screen and (max-width: ${breakpoints.phoneMax}) {
      margin: 0 20px;
    }
  }
`

const DetailsWrapper = styled.div`
  border-top: 0;
  padding-top: 32px;

  > div:last-of-type {
    border-bottom: none;
  }

  ${props =>
    props.preview &&
    `
    > div {
      padding: 0;
    }
  `};

  @media print {
    border: none;
  }
`

const DetailSection = styled.div`
  margin: 0 0 20px;
  border-bottom: 1px solid ${colors.mediumLightGrey};
  padding: 0 52px;

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    padding: 0 20px;
  }

  > :last-child {
    border-bottom: none;
  }
`

const SectionTitle = styled.h4`
  color: ${colors.darkGrey};
  font-size: 28px;
  letter-spacing: -0.08px;
  line-height: 32px;
  font-weight: ${fontWeights.medium};
  margin: 10px 0 3px;
  @media screen and (max-width: ${breakpoints.phoneMax}) {
    font-size: 18px;
    font-weight: ${fontWeights.bold};
    letter-spacing: -0.04px;
    line-height: 20px;
  }
`

const RatingValue = styled.div`
  margin-left: auto;
  font-size: 14px;
  letter-spacing: 0.07px;
  line-height: 16px;

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    margin: 16px 0 0;
  }
`

const RatingScore = styled.span`
  color: ${colors.darkGrey};
  font-size: 24px;
  font-weight: ${fontWeights.bold};
  letter-spacing: -0.39px;
  line-height: 28px;
`

const RatingMax = styled.span`
  color: ${colors.mediumGrey};
  margin-left: 4px;
`

const LivCardDescription = styled.p`
  font-size: 14px;
  letter-spacing: 0.07px;
  line-height: 16px;
  margin: 20px 0 32px;
  @media screen and (max-width: ${breakpoints.phoneMax}) {
    margin: 16px 0 20px;
  }
`

const ItemWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin: 24px 0 8px;
  padding-bottom: 24px;
  border-bottom: 1px dashed #d9d7d8;
  gap: 16px 0;

  :last-child {
    margin-bottom: 20px;
    @media screen and (max-width: ${breakpoints.phoneMax}) {
      margin-bottom: 0;
    }
  }
`

const Item = styled.dl`
  display: flex;
  justify-content: space-between;
`

const ItemLabel = styled.dt`
  font-size: ${fontSizes.regular};
  font-weight: ${fontWeights.medium};
  letter-spacing: -0.32px;
  line-height: 21px;
  margin: 0;
  display: flex;
  align-items: center;
`

const ItemValue = styled.dd`
  font-size: ${fontSizes.regular};
  font-weight: ${fontWeights.medium};
  letter-spacing: -0.4px;
  line-height: 20px;
  color: ${({ color }) => color};
  margin: 0;
`

const RecordWrapper = styled.div`
  display: flex;
  align-items: center;
  margin: 0;
  gap: 0 4px;

  p {
    font-size: 14px;
    line-height: 18px;
    margin: 0;
  }

  svg {
    margin-right: 4px;
  }
`

const LivCardDescriptionWrapper = styled.div`
  border-bottom: 1px dashed #d9d7d8;
  ${props =>
    props.$height &&
    `
    height: ${props.$height}px;
  `};
`

const TitleBadgeWrapper = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 0 16px;
  @media screen and (max-width: ${breakpoints.phoneMax}) {
    width: 100%;
    justify-content: space-between;
    gap: 0 12px;
  }
`

const TitleWrapper = styled(TitleBadgeWrapper)`
  @media screen and (max-width: ${breakpoints.phoneMax}) {
    flex-direction: column;
    align-items: flex-start;
  }
`

const LinkButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: ${colors.secondary};
  font-size: 15px;
  line-height: 16px;
  letter-spacing: -0.4px;
  text-decoration-line: underline;
  padding: 0;
  margin-left: 8px;
`

const ViewDocument = ({ file, disabled }) => {
  const { t } = useTranslation()
  if (!file) return null
  return (
    <LinkButton
      type="button"
      onClick={file.onClick}
      id={file.clickId}
      disabled={disabled}
      tabIndex={disabled ? -1 : undefined}
    >
      {t('b.liv_score_card.view.document.button', 'View Document')}
    </LinkButton>
  )
}

const GOOD = '#59B17D'
const BAD = '#DC0027'
const OK = '#F7C40A'
const NEUTRAL = '#9B9B9B'

const COLORS = {
  // Credit score, Collections + Bankruptcy: excellent, very good, average, poor, pending?
  excellent: GOOD,
  average: OK,
  poor: BAD,
  // Trustworthiness, ID + Employment + Income + Social: not_verified, verified, pending
  // Can afford: verified, not_verified
  verified: GOOD,
  not_verified: BAD,
  // Court: failed, passed, pending
  failed: BAD,
  passed: GOOD,
  // All
  pending: NEUTRAL
}

const Check = () => <Icon id="check_item" width={14} height={12} />

const CardDetails = ({
  data,
  userRole,
  professionId,
  csoVerifiedState,
  salarySort,
  listingData,
  secureFiles,
  preview
}) => {
  const { t } = useTranslation()
  const findDocument = documentType =>
    find(file => file.document_type === documentType, secureFiles)
  const SCORES = {
    excellent: t('b.liv_score_card.view.excellent.text', 'Excellent'),
    average: t('b.liv_score_card.view.average.text', 'Average'),
    'very good': t('b.liv_score_card.view.very_good.text', 'Very Good'),
    poor: t('b.liv_score_card.view.poor.text', 'Poor'),
    verified: t('common.verified', 'Verified'),
    not_verified: t('b.liv_score_card.view.not_verified.text', 'Not Verified'),
    failed: t('b.liv_score_card.view.failed.text', 'Failed'),
    passed: t('common.verified', 'Verified'),
    pending: t('b.liv_score_card.view.pending.text', 'Pending...')
  }

  const CSOSCORES = {
    pending: t('b.liv_score_card.view.pending.text', 'Pending...'),
    passed: t('b.liv_score_card.view.not_found.text', 'Not Found'),
    failed: t('b.liv_score_card.view.found.text', 'Found')
  }

  const LABELS = {
    payments: { text: t('b.liv_score_card.view.credit.label', 'Credit') },
    payment: {
      text: t('b.liv_score_card.view.payment_record.label', 'Payment Record'),
      modal: PaymentRecord
    },
    bankruptcy: {
      text: t('b.liv_score_card.view.bankruptcy_index.label', 'Bankruptcy Index'),
      modal: BankruptcyIndex
    },
    collections: {
      text: t('b.liv_score_card.view.collections_record.label', 'Collections Record'),
      modal: CollectionsRecord
    },
    credit_utilization: {
      text: t('b.liv_score_card.view.credit_utilization.label', 'Credit Utilization'),
      modal: CreditUtilization
    },
    trustworthiness: { text: t('b.liv_score_card.view.trustworthiness.label', 'Trustworthiness') },
    employers: {
      text: t('b.liv_score_card.view.employment_verification.label', 'Employment Verification'),
      modal: EmploymentVerification
    },
    income: {
      text: t('b.liv_score_card.view.income_verification.label', 'Income Verification'),
      modal: IncomeVerification
    },
    social_media: {
      text: t('b.liv_score_card.view.social_verification.label', 'Social Verification'),
      modal: SocialVerification
    },
    verified_id: {
      text: t('b.liv_score_card.view.identity_verification.label', 'Identity Verification'),
      modal: IdentityVerification
    },
    income_verfication: {
      text: t('b.liv_score_card.view.income_verification.label', 'Income Verification')
    },
    income_to_rent_ratio: {
      text: t('b.liv_score_card.view.income_to_rent_ratio.label', 'Income-to-Rent Ratio')
    },
    court_record_verification: {
      text: t('b.liv_score_card.view.court_record.label', 'Court Record Verification')
    }
  }

  const DESCRIPTIONS = {
    payments: {
      landlord: t(
        'l.liv_score_card.view.credit_description.text',
        `This score is an extensive analysis from the last 4 years of the renter's credit history on all accounts.`
      ),
      tenant: t(
        'r.liv_score_card.view.credit_description.text',
        'This score is an extensive analysis from the last 4 years of your credit history on all accounts.'
      )
    },
    trustworthiness: {
      landlord: t(
        'l.liv_score_card.view.trustworthiness_description.text',
        'Renter’s trustworthiness is determined by the following metrics to ensure their integrity.'
      ),
      tenant: t(
        'r.liv_score_card.view.trustworthiness_description.text',
        'Your trustworthiness is determined by the following metrics to ensure user integrity.'
      )
    },
    income_to_rent_ratio: {
      landlord: t(
        'l.liv_score_card.view.income_to_rent_ratio_description.text',
        "To achieve a passing score, the rent price for the suite should be 1/3 of renter's self-reported monthly income."
      )
    },
    court_record_verification: {
      landlord: t(
        'b.liv_score_card.view.court_record_verification.text',
        'If records of possible infractions are detected on public provincial/state court records, the results will be indicated here.'
      ),
      tenant: t(
        'b.liv_score_card.view.court_record_verification.text',
        'If records of possible infractions are detected on public provincial/state court records, the results will be indicated here.'
      )
    }
  }

  const DETAILS = {
    records: (doc, number) => (
      <RecordWrapper key="records">
        <Check />
        <p>
          {t('b.liv_score_card.view.bankruptcy_record_count.label', {
            defaultValue: '{{count}} Record Found',
            defaultValue_plural: '{{count}} Records Found',
            count: number
          })}
        </p>
      </RecordWrapper>
    ),
    accounts: (doc, number) => (
      <RecordWrapper key="accounts">
        <Check />
        <p>
          {t('b.liv_score_card.view.accounts_count.label', {
            defaultValue: '{{count}} Account Checked',
            defaultValue_plural: '{{count}} Accounts Checked',
            count: number
          })}
        </p>
      </RecordWrapper>
    ),
    t4: doc => (
      <RecordWrapper key="t4">
        <Check />
        <p>{t('b.liv_score_card.view.t4.label', 'T4')}</p>
        <ViewDocument file={findDocument(doc)} disabled={preview} />
      </RecordWrapper>
    ),
    job_offer: doc => (
      <RecordWrapper key="job_offer">
        <Check />
        <p>{t('b.liv_score_card.view.job_offer.label', 'Job Offer')}</p>
        <ViewDocument file={findDocument(doc)} disabled={preview} />
      </RecordWrapper>
    ),
    payslip: doc => (
      <RecordWrapper key="payslip">
        <Check />
        <p>{t('b.liv_score_card.view.payslip.label', 'Pay Slip')}</p>
        <ViewDocument file={findDocument(doc)} disabled={preview} />
      </RecordWrapper>
    ),
    student_loan: doc => (
      <RecordWrapper key="student_load">
        <Check />
        <p>{t('b.liv_score_card.view.student_loan.label', 'Student Loan')}</p>
        <ViewDocument file={findDocument(doc)} disabled={preview} />
      </RecordWrapper>
    ),
    school_letter: doc =>
      professionId === 'student' && (
        <RecordWrapper key="school_letter">
          <Check />
          <p>{t('b.liv_score_card.view.school_letter.label', 'Enrolment Letter')}</p>
          <ViewDocument file={findDocument(doc)} disabled={preview} />
        </RecordWrapper>
      ),
    school_id: doc =>
      professionId === 'student' && (
        <RecordWrapper key="school_id">
          <Check />
          <p>{t('b.liv_score_card.view.school_id.label', 'School ID')}</p>
          <ViewDocument file={findDocument(doc)} disabled={preview} />
        </RecordWrapper>
      ),
    bank_statement: doc => (
      <RecordWrapper key="bank_statement">
        <Check />
        <p>{t('b.liv_score_card.view.bank_statement.label', 'Bank Statement')}</p>
        <ViewDocument file={findDocument(doc)} disabled={preview} />
      </RecordWrapper>
    ),
    business_card: doc => (
      <RecordWrapper key="business_card">
        <Check />
        <p>{t('b.liv_score_card.view.business_card.label', 'Business Card')}</p>
        <ViewDocument file={findDocument(doc)} disabled={preview} />
      </RecordWrapper>
    ),
    tax_slip: doc => (
      <RecordWrapper key="tax_slip">
        <Check />
        <p>{t('b.liv_score_card.view.tax_slip.label', 'Tax Slips')}</p>
        <ViewDocument file={findDocument(doc)} disabled={preview} />
      </RecordWrapper>
    ),
    passport: doc => (
      <RecordWrapper key="passport">
        <Check />
        <p>{t('b.liv_score_card.view.passport.label', 'Passport')}</p>
        <ViewDocument file={findDocument(doc)} disabled={preview} />
      </RecordWrapper>
    ),
    permanent_resident: doc => (
      <RecordWrapper key="permanent_resident">
        <Check />
        <p>{t('b.liv_score_card.view.permanent_resident.label', 'Permanent Resident')}</p>
        <ViewDocument file={findDocument(doc)} disabled={preview} />
      </RecordWrapper>
    ),
    health_insurance: doc => (
      <RecordWrapper key="health_insurance">
        <Check />
        <p>{t('b.liv_score_card.view.health_insurance.label', 'Health Insurance')}</p>
        <ViewDocument file={findDocument(doc)} disabled={preview} />
      </RecordWrapper>
    ),
    drivers_license: doc => (
      <RecordWrapper key="drivers_license">
        <Check />
        <p>{t('b.liv_score_card.view.drivers_license.label', `Driver's Licence`)}</p>
        <ViewDocument file={findDocument(doc)} disabled={preview} />
      </RecordWrapper>
    ),
    selfie: doc => (
      <RecordWrapper key="selfie">
        <Check />
        <p>{t('b.liv_score_card.view.selfie.label', 'Selfie Check')}</p>
        <ViewDocument file={findDocument(doc)} disabled={preview} />
      </RecordWrapper>
    )
  }

  const getLabel = key => LABELS[key].text || key
  const getInfoModal = key => {
    const InfoModalComponent = LABELS[key].modal
    return InfoModalComponent ? <InfoModalComponent disabled={preview} /> : null
  }

  const getInfo = (item, label) => {
    const documents = toPairs(item.details[label])
    return documents.map(doc => {
      if (doc[1] === false) return null
      return doc[1] !== null ? DETAILS[doc[0]](doc[0], doc[1]) : null
    })
  }

  const getScore = key => SCORES[key] || '-'
  const getCSOScore = key => CSOSCORES[key] || '-'
  const getColors = key => COLORS[key] || COLORS['pending']

  const getDescriptionByRole = key => DESCRIPTIONS[key][userRole]

  const mapVerification = (item, key) =>
    !Array.isArray(path(['details', key], item)) && !!path(['details', key], item)

  const getCanAfford = label => {
    const dateConversion = {
      monthly: 12,
      weekly: 52,
      daily: 365
    }

    const incomeRatio =
      (listingData.price * dateConversion[listingData.price_frequency]) / Number(salarySort)
    const canAfford = incomeRatio < 0.3

    return Number(salarySort) ? (
      <BubbleTitle color={canAfford ? 'verified' : 'not_verified'} label={label}>
        {canAfford
          ? t('b.component_liv_score_application_card.view.pass.text', 'Pass')
          : t('b.component_liv_score_application_card.view.failed.text', 'Failed')}
      </BubbleTitle>
    ) : (
      <BubbleTitle color="pending" label={label}>
        {t('b.component_liv_score_application_card.view.no_salary.text', 'No Salary')}
      </BubbleTitle>
    )
  }
  const sections = toArray(data?.breakdown)
  const isTablet = useMediaQuery(`(max-width: ${breakpoints.ipadMiniMax})`)
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.phoneMax})`)
  const isSmallMobile = useMediaQuery(`(max-width: 405px)`)
  const height = (() => {
    if (isSmallMobile) return 162
    if (isMobile) return 146
    if (isTablet) return 130
    return 114
  })()
  return (
    <StyledReveal isOpen={!preview} closedHeight={`${height + 32}px`}>
      <DetailsWrapper preview={preview}>
        {sections.map((item, index) => (
          <DetailSection
            key={item.category_name}
            aria-hidden={preview && item.category_name !== 'payments'}
          >
            <LivCardDescriptionWrapper $height={index <= 0 ? height : undefined}>
              <TitleWrapper>
                <TitleBadgeWrapper>
                  <SectionTitle>{getLabel(item.category_name)}</SectionTitle>
                  <BubbleTitle color={item.message} label={getLabel(item.category_name)}>
                    {getScore(item.message)}
                  </BubbleTitle>
                </TitleBadgeWrapper>
                <RatingValue
                  aria-label={t('b.liv_score_card.section_points_summary.label', {
                    defaultValue: '{{name}}: {{score}} points out of {{max}}',
                    name: getLabel(item.category_name),
                    score: item.score,
                    max: item.maximum
                  })}
                >
                  <RatingScore>{item.score} </RatingScore>
                  {t('b.liv_score_card.view.points.text', 'pts')}
                  <RatingMax>/ {item.maximum}</RatingMax>
                </RatingValue>
              </TitleWrapper>
              <LivCardDescription>{getDescriptionByRole(item.category_name)}</LivCardDescription>
            </LivCardDescriptionWrapper>
            {!!item.labels &&
              Object.keys(item.labels).map((label, key) => (
                <ItemWrapper key={key}>
                  <Item>
                    <ItemLabel>
                      {getLabel(label)} {getInfoModal(label)}
                    </ItemLabel>
                    <ItemValue color={getColors(item.labels[label])}>
                      {getScore(item.labels[label])}
                    </ItemValue>
                  </Item>
                  {mapVerification(item, label) && getInfo(item, label)}
                </ItemWrapper>
              ))}
          </DetailSection>
        ))}
        {!!listingData && (
          <DetailSection aria-hidden={preview}>
            <TitleBadgeWrapper>
              <SectionTitle>{getLabel('income_to_rent_ratio')}</SectionTitle>
              {getCanAfford(getLabel('income_to_rent_ratio'))}
            </TitleBadgeWrapper>
            <LivCardDescription>
              {t(
                'l.liv_score_card.view.income_to_rent_ratio_description.text',
                "To achieve a passing score, the rent price for the suite should be 1/3 of renter's self-reported monthly income."
              )}
            </LivCardDescription>
          </DetailSection>
        )}
        {!!csoVerifiedState && (
          <DetailSection aria-hidden={preview}>
            <TitleBadgeWrapper>
              <SectionTitle>{getLabel('court_record_verification')}</SectionTitle>
              <BubbleTitle color={csoVerifiedState} label={getLabel('court_record_verification')}>
                {getCSOScore(csoVerifiedState)}
              </BubbleTitle>
            </TitleBadgeWrapper>
            <LivCardDescription>
              {t(
                'b.liv_score_card.view.court_record_verification.text',
                'If records of possible infractions are detected on public provincial/state court records, the results will be indicated here.'
              )}
            </LivCardDescription>
          </DetailSection>
        )}
      </DetailsWrapper>
    </StyledReveal>
  )
}

export default CardDetails
